import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  Activity, BellRing, Check, ChevronLeft, ClipboardCopy, Dna, Droplets, Gamepad2, KeyRound,
  Link2, ListChecks, Mail, MapPin, Package, RefreshCw, Search, Send, ShoppingBag,
  Smartphone, Soup, Trophy, Unlink, UserRound, X,
} from 'lucide-react';
import { FaApple, FaGoogle } from 'react-icons/fa6';

import {
  linkMobileAccountPatient, loadMobileAccount, resendMobileInvitation, searchPatients,
} from './api';
import { Empty, Failed, Loading, Pill } from './parts';
import { tx } from './i18n';
import { codeLabel, orderReferenceLabel } from './labels';
import { useLiveResource } from './useLive';
import { useXomicsAuth } from './AuthContext';
import { errorMessage } from './ui';
import { PatientDetailModal } from './PatientDetailModal';
import { sessionVisits, useAppStrings } from './AppPhone';
import SessionPlayer, { ScreenIcon } from './SessionPlayer';
import DeviceHealth from './DeviceHealth';
import { DeviceArt, deviceLook, finishLabel } from './DeviceArt';
import {
  BatteryMark, CHART, COMPACT, ChartTip, Flag, HourHeatmap, NUMBER, Panel, PlatformMark, PulseMap, PushCard,
  ago, averageOf, counted, day, emailReasonLabel, initials, lengthOf, localClock, metricLabel, originLabel, percent, pushTypeLabel, span,
  stackRows, statusTone, tipDay, useNow, versionLabel, versusAverage, when,
} from './MobileParts';

const ORIGIN_COLORS = { r01: CHART.ink, b01: CHART.mid, apple: CHART.soft, android: '#c9d1e6', manual: CHART.pale };
const OUTCOMES = { success: CHART.ink, timeout: CHART.soft, error: '#d9957f', aborted: CHART.pale };
const MINUTE = 60000;
const DAY = 86400000;
const LIVE_MS = 5 * MINUTE;
const FEED_SHORT = 12;
const FEED_LONG = 60;

// The page's sections as tabs along the bottom of the header, grouped the way
// people think about someone: what they do in the app, their health, what they
// bought, and their records.
const SECTIONS = [
  [['overview', 'ui.mobile.tab_overview'], ['sessions', 'ui.mobile.tab_sessions']],
  [['devices', 'ui.mobile.tab_health'], ['diet', 'ui.mobile.tab_diet'], ['habits', 'ui.mobile.tab_habits'],
    ['workouts', 'ui.mobile.tab_workouts'], ['games', 'ui.mobile.tab_games']],
  [['orders', 'ui.mobile.tab_orders'], ['kits', 'ui.mobile.tab_kits'], ['payments', 'ui.mobile.tab_payments']],
  [['messages', 'ui.mobile.notifications_title'], ['security', 'ui.mobile.tab_security']],
];

function SectionNav({ value, onChange, counts, hidden }) {
  return (
    <nav className="xo-mob-profile-nav" aria-label={tx('ui.mobile.profile_sections')}>
      {SECTIONS.map((group) => group.filter(([key]) => !hidden.has(key))).filter((group) => group.length > 0).map((group) => (
        <div key={group[0][0]} className="xo-mob-profile-nav-group">
          {group.map(([key, label]) => (
            <button type="button" key={key} className={value === key ? 'is-active' : ''} aria-current={value === key ? 'page' : undefined} onClick={() => onChange(key)}>
              {tx(label)}
              {counts[key] !== undefined && <b>{COMPACT.format(counts[key])}</b>}
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
}

function ageFrom(dateOfBirth) {
  if (!dateOfBirth) return null;
  const born = new Date(`${dateOfBirth}T00:00:00Z`);
  if (Number.isNaN(born.getTime())) return null;
  const now = new Date();
  let age = now.getUTCFullYear() - born.getUTCFullYear();
  if (now.getUTCMonth() < born.getUTCMonth() || (now.getUTCMonth() === born.getUTCMonth() && now.getUTCDate() < born.getUTCDate())) age -= 1;
  return age;
}

// An amount in its currency's smallest unit, as money in the viewer's format.
function money(minor, currency) {
  if (minor === null || minor === undefined || !currency) return '—';
  try {
    const format = new Intl.NumberFormat(undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' });
    return format.format(minor / 10 ** format.resolvedOptions().maximumFractionDigits);
  } catch {
    return `${currency} ${(minor / 100).toLocaleString()}`;
  }
}

// Order numbers read in groups of four, as on the Orders page; anything else as it is.
function reference(code) {
  try {
    return orderReferenceLabel(code);
  } catch {
    return code || '—';
  }
}

// The app's own name for a game or a workout; the code in words for the few old
// games the app no longer names.
function appName(as, key, code) {
  const text = as(key);
  return text.startsWith('⟦') ? String(code).replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2') : text;
}
const gameName = (code, as) => appName(as, `games.${code}`, code);
const workoutName = (code, as) => appName(as, `activity.type.${code}`, code);
// Workouts come in whole minutes; one stopped straight away is under a minute, not "0 sec".
function workoutLength(minutes) {
  if (minutes === null) return tx('ui.mobile.still_going');
  return minutes === 0 ? tx('ui.mobile.under_minute') : lengthOf(minutes * MINUTE);
}

const MEALS = { breakfast: 'nutrition.breakfast', lunch: 'nutrition.lunch', dinner: 'nutrition.dinner', snack: 'nutrition.snacks', snacks: 'nutrition.snacks' };
function mealName(type, as) {
  const key = MEALS[String(type || '').toLowerCase()];
  return key ? as(key) : String(type || '');
}

function dayHeading(at, now) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (at >= start.getTime()) return tx('ui.mobile.today');
  if (at >= start.getTime() - DAY) return tx('ui.mobile.yesterday');
  return new Date(at).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

function clock(at) {
  return new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function dateOf(at) {
  return at === null || at === undefined ? '—' : new Date(at).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function byDay(items, now) {
  const groups = [];
  for (const item of items) {
    const heading = dayHeading(item.at, now);
    if (groups.length === 0 || groups[groups.length - 1].heading !== heading) groups.push({ heading, items: [] });
    groups[groups.length - 1].items.push(item);
  }
  return groups;
}

function Copy({ value, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" className="xo-mob-copy" title={label}
      onClick={async () => {
        try { await navigator.clipboard.writeText(value); setCopied(true); window.setTimeout(() => setCopied(false), 1400); } catch { /* clipboard unavailable */ }
      }}>
      {copied ? <Check aria-hidden="true" /> : <ClipboardCopy aria-hidden="true" />}
    </button>
  );
}

function Facts({ rows }) {
  return (
    <dl className="xo-mob-facts">
      {rows.filter(([, value]) => value !== null && value !== undefined && value !== '').map(([label, value]) => (
        <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
      ))}
    </dl>
  );
}

// A strip of headline numbers inside a section; a tile with somewhere to go opens that section.
function Tiles({ items, onOpen = null }) {
  return (
    <div className="xo-mob-fact-grid" style={{ '--xo-mob-tiles': Math.min(4, items.length) }}>
      {items.map((item) => {
        const inner = <><small>{item.label}</small><strong>{item.value}</strong>{item.detail && <em>{item.detail}</em>}</>;
        return item.go && onOpen
          ? <button type="button" key={item.label} className="xo-mob-fact is-link" onClick={() => onOpen(item.go)}>{inner}</button>
          : <div key={item.label} className="xo-mob-fact">{inner}</div>;
      })}
    </div>
  );
}

// Picks another patient for this account, or unlinks it. The mobile backend
// refuses a patient that already belongs to another account.
function PatientLinkDialog({ account, current, onClose, onChanged }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState({ status: 'idle', items: [] });
  const [chosen, setChosen] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  useEffect(() => {
    const query = term.trim();
    if (query.length < 3) { setResults({ status: 'idle', items: [] }); return undefined; }
    const controller = new AbortController();
    setResults((previous) => ({ ...previous, status: 'loading' }));
    const timer = window.setTimeout(async () => {
      try {
        const found = await searchPatients(query, { limit: 8, signal: controller.signal });
        setResults({ status: 'ready', items: found.items });
      } catch (caught) {
        if (caught.name !== 'AbortError') setResults({ status: 'failed', items: [], error: errorMessage(caught) });
      }
    }, 260);
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [term]);

  async function save(patientId) {
    setSaving(true);
    setError(null);
    try {
      await linkMobileAccountPatient(account.uuid, patientId);
      onChanged();
      onClose();
    } catch (caught) {
      setError(caught.code === 'PATIENT_LINKED_ELSEWHERE' ? tx('ui.mobile.patient_linked_elsewhere') : errorMessage(caught));
      setSaving(false);
    }
  }

  return (
    <div className="xo-mob-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.div className="xo-mob-dialog is-wide" role="dialog" aria-modal="true" aria-labelledby="xo-mob-link-title"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
        <header>
          <span className="xo-mob-dialog-icon"><Link2 aria-hidden="true" /></span>
          <div><h2 id="xo-mob-link-title">{tx('ui.mobile.change_patient_title')}</h2><p>{tx('ui.mobile.change_patient_detail')}</p></div>
          <button type="button" className="xo-mob-icon-button" aria-label={tx('ui.mobile.close')} onClick={onClose}><X /></button>
        </header>
        <div className="xo-mob-form">
          {current && (
            <div className="xo-mob-current-link">
              <small>{tx('ui.mobile.currently_linked')}</small>
              <strong>{current.name || `#${current.id}`}</strong>
              <button type="button" className="xo-btn-2" disabled={saving} onClick={() => save(null)}><Unlink aria-hidden="true" />{tx('ui.mobile.unlink')}</button>
            </div>
          )}
          <label className="xo-mob-search-input">
            <Search aria-hidden="true" />
            <input value={term} onChange={(event) => setTerm(event.target.value)} placeholder={tx('ui.mobile.search_patients')} autoFocus />
          </label>
          <div className="xo-mob-patient-results">
            {results.status === 'idle' && <small className="is-muted">{tx('ui.mobile.search_patients_hint')}</small>}
            {results.status === 'loading' && <small className="is-muted">{tx('ui.mobile.searching')}</small>}
            {results.status === 'failed' && <p className="xo-alert t-warn">{results.error}</p>}
            {results.status === 'ready' && results.items.length === 0 && <small className="is-muted">{tx('ui.mobile.no_patients_found')}</small>}
            {results.items.map((patient) => (
              <button type="button" key={patient.id} className={chosen?.id === patient.id ? 'is-chosen' : ''} onClick={() => setChosen(patient)}>
                <span className="xo-mob-avatar is-small">{initials(patient.name, patient.email)}</span>
                <span><strong>{patient.name}</strong>
                  <small>{[`#${patient.id}`, patient.dateOfBirth, patient.personalId, patient.email].filter(Boolean).join(' · ')}</small></span>
                {current?.id === patient.id && <Pill tone="info">{tx('ui.mobile.current')}</Pill>}
              </button>
            ))}
          </div>
          {error && <p className="xo-alert t-warn" role="alert">{error}</p>}
          <div className="xo-mob-dialog-actions">
            <button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.mobile.cancel')}</button>
            <button type="button" className="xo-btn" disabled={!chosen || saving || current?.id === chosen.id} onClick={() => save(chosen.id)}>
              <Link2 aria-hidden="true" />{chosen ? tx('ui.mobile.link_to', { name: chosen.name }) : tx('ui.mobile.choose_patient')}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------- the header

// Which patient this person is, in one line on the right of the header, with the way into the record.
function PatientBox({ data, mayManage, onLink, onOpenRecord }) {
  const patient = data.patient;
  const change = mayManage && (
    <button type="button" className="xo-mob-icon-button" onClick={onLink} title={tx('ui.mobile.change_patient')} aria-label={tx('ui.mobile.change_patient')}>
      <Link2 aria-hidden="true" />
    </button>
  );
  if (patient === null) {
    return (
      <aside className="xo-mob-patient-box is-empty">
        <span className="xo-mob-patient-box-text"><strong>{tx('ui.mobile.no_patient_linked')}</strong><small>{tx('ui.mobile.patient')}</small></span>
        {mayManage && <button type="button" className="xo-btn-2" onClick={onLink}><Link2 aria-hidden="true" />{tx('ui.mobile.link_patient')}</button>}
      </aside>
    );
  }
  if (patient.restricted || patient.missing) {
    return (
      <aside className="xo-mob-patient-box is-empty">
        <span className="xo-mob-patient-box-text">
          <strong>{tx('ui.mobile.patient_number', { id: patient.id })}</strong>
          <small>{patient.missing ? tx('ui.mobile.patient_missing') : tx('ui.mobile.patient_restricted')}</small>
        </span>
        {change}
      </aside>
    );
  }
  const age = ageFrom(patient.dateOfBirth);
  return (
    <aside className="xo-mob-patient-box">
      <span className="xo-mob-avatar">{initials(patient.name, patient.email)}</span>
      <span className="xo-mob-patient-box-text">
        <strong>{patient.name}</strong>
        <small>{[tx('ui.mobile.patient'), patient.code, patient.project.name, age === null ? null : tx('ui.mobile.age_years', { age }),
          patient.gender ? codeLabel('gender', patient.gender) : null].filter(Boolean).join(' · ')}</small>
      </span>
      <span className="xo-mob-actions">
        <button type="button" className="xo-btn" onClick={onOpenRecord}><Dna aria-hidden="true" />{tx('ui.mobile.open_patient_record')}</button>
        {change}
      </span>
    </aside>
  );
}

// ---------------------------------------------------------------- overview

// Everything the person did or was sent, newest first, in one list.
function activityFeed(data, commerce, as) {
  const items = [];
  for (const session of data.activity.sessions) {
    const visits = sessionVisits(session).filter((visit) => visit.screen !== 'launch');
    items.push({ at: session.end, Icon: Smartphone, go: 'sessions', title: tx('ui.mobile.feed_session', { length: lengthOf(session.end - session.start) }),
      detail: counted('ui.mobile.screens_count', visits.length), screens: visits.slice(0, 6).map((visit) => visit.screen) });
  }
  for (const meal of data.diet.meals) {
    items.push({ at: meal.at, Icon: Soup, go: 'diet', title: tx('ui.mobile.feed_meal', { meal: mealName(meal.type, as) }),
      detail: [meal.kcal === null ? null : tx('ui.mobile.value_kcal', { value: NUMBER.format(Math.round(meal.kcal)) }), counted('ui.mobile.items_count', meal.items.length)].filter(Boolean).join(' · ') });
  }
  for (const tick of data.habits.recent) items.push({ at: tick.at, Icon: ListChecks, go: 'habits', title: tx('ui.mobile.feed_habit', { habit: tick.name }) });
  for (const play of data.games.recent) {
    items.push({ at: play.at, Icon: Gamepad2, go: 'games', title: tx('ui.mobile.feed_game', { game: gameName(play.game, as) }), detail: tx('ui.mobile.score_value', { score: NUMBER.format(play.score) }) });
  }
  for (const workout of data.workouts) {
    items.push({ at: workout.startedAt, Icon: Activity, go: 'workouts', title: tx('ui.mobile.feed_workout', { activity: workoutName(workout.type, as) }),
      detail: workoutLength(workout.minutes) });
  }
  for (const device of data.devices) {
    if (device.lastSuccessAt !== null) items.push({ at: device.lastSuccessAt, Icon: RefreshCw, go: 'devices', title: tx('ui.mobile.feed_sync', { device: originLabel(device.origin) }) });
  }
  for (const push of data.notifications) {
    if (!push.silent) items.push({ at: push.at, Icon: BellRing, go: 'messages', title: tx('ui.mobile.feed_push', { title: push.title || pushTypeLabel(push.type) }) });
  }
  for (const message of data.messages) {
    items.push({ at: message.at, Icon: Mail, go: 'messages', title: tx('ui.mobile.feed_email', { subject: message.subject || emailReasonLabel(message.reason) }) });
  }
  for (const order of commerce.orders) {
    items.push({ at: order.placedAt || order.createdAt, Icon: ShoppingBag, go: 'orders', title: tx('ui.mobile.feed_order', { total: money(order.totalMinor, order.currency) }),
      detail: order.items.map((item) => item.name).filter(Boolean).join(', ') });
  }
  for (const kit of commerce.kits) {
    if (kit.activatedAt !== null) items.push({ at: kit.activatedAt, Icon: Package, go: 'kits', title: tx('ui.mobile.feed_kit', { product: kit.product || tx('ui.mobile.kit') }) });
  }
  items.push({ at: data.account.createdAt, Icon: UserRound, go: null, title: tx('ui.mobile.feed_joined') });
  return items.filter((item) => item.at !== null && item.at !== undefined).sort((a, b) => b.at - a.at);
}

function Feed({ items, now, onOpen }) {
  const [long, setLong] = useState(false);
  const shown = items.slice(0, long ? FEED_LONG : FEED_SHORT);
  return (
    <div className="xo-mob-feed">
      {byDay(shown, now).map((group) => (
        <div key={group.heading} className="xo-mob-feed-day">
          <h4>{group.heading}</h4>
          <ol>
            {group.items.map((item, index) => {
              const body = (
                <>
                  <span className="xo-mob-feed-icon"><item.Icon aria-hidden="true" /></span>
                  <span className="xo-mob-feed-main">
                    <strong>{item.title}</strong>
                    {(item.detail || item.screens) && (
                      <small>
                        {item.detail}
                        {item.screens && item.screens.length > 0 && (
                          <span className="xo-mob-feed-screens" aria-hidden="true">{item.screens.map((screen, position) => <ScreenIcon key={position} screen={screen} />)}</span>
                        )}
                      </small>
                    )}
                  </span>
                  <time>{clock(item.at)}</time>
                </>
              );
              return (
                <li key={`${item.at}-${index}`}>
                  {item.go ? <button type="button" onClick={() => onOpen(item.go)}>{body}</button> : <span>{body}</span>}
                </li>
              );
            })}
          </ol>
        </div>
      ))}
      {items.length > FEED_SHORT && (
        <button type="button" className="xo-mob-more" onClick={() => setLong(!long)}>
          {long ? tx('ui.mobile.show_fewer') : tx('ui.mobile.show_more_activity', { count: NUMBER.format(Math.min(items.length, FEED_LONG) - FEED_SHORT) })}
        </button>
      )}
    </div>
  );
}

function OverviewSection({ data, commerce, now, as, onOpen }) {
  const paired = data.devices.filter((device) => device.status === 'paired');
  const lastSync = Math.max(0, ...data.devices.map((device) => (device.lastSuccessAt === null ? 0 : device.lastSuccessAt)));
  const sessions = data.activity.sessions;
  const logged = data.diet.days.filter((entry) => entry.meals > 0);
  const plays = data.games.totals.reduce((sum, game) => sum + game.plays, 0);
  const minutes = data.workouts.reduce((sum, workout) => sum + (workout.minutes === null ? 0 : workout.minutes), 0);
  const feed = useMemo(() => activityFeed(data, commerce, as), [data, commerce, as]);
  const pings = data.activity.places.filter((place) => place.lat !== null).map((place) => ({
    at: place.lastSeenAt, city: place.city, country: place.country, lat: place.lat, lng: place.lng,
  }));
  const tiles = [
    { label: tx('ui.mobile.sessions_90d'), value: NUMBER.format(sessions.length), detail: sessions[0] ? tx('ui.mobile.latest_session_ago', { when: ago(sessions[0].end, now) }) : null, go: 'sessions' },
    { label: tx('ui.mobile.paired_wearables'), value: NUMBER.format(paired.length), detail: lastSync > 0 ? tx('ui.mobile.synced_ago', { when: ago(lastSync, now) }) : null, go: 'devices' },
    { label: tx('ui.mobile.meals_30d'), value: NUMBER.format(data.diet.meals.length), detail: counted('ui.mobile.days_logged', logged.length), go: 'diet' },
    { label: tx('ui.mobile.habits_done_30d'), value: NUMBER.format(data.life.completions30), detail: counted('ui.mobile.habits_active', data.habits.list.filter((habit) => habit.removedAt === null).length), go: 'habits' },
    { label: tx('ui.mobile.workouts'), value: NUMBER.format(data.workouts.length), detail: minutes > 0 ? lengthOf(minutes * MINUTE) : null, go: 'workouts' },
    { label: tx('ui.mobile.games_played'), value: NUMBER.format(plays), detail: counted('ui.mobile.games_count', data.games.totals.length), go: 'games' },
    { label: tx('ui.mobile.ai_messages'), value: NUMBER.format(data.life.aiTurns), detail: data.life.lastAiAt ? ago(data.life.lastAiAt, now) : null },
    commerce.mayReadOrders
      ? { label: tx('ui.mobile.tab_orders'), value: NUMBER.format(commerce.orders.length), detail: commerce.orders[0] ? ago(commerce.orders[0].placedAt || commerce.orders[0].createdAt, now) : null, go: 'orders' }
      : { label: tx('ui.mobile.clinical_entries'), value: NUMBER.format(data.life.clinicalEntries) },
  ];
  return (
    <>
      <div className="xo-mob-row is-wide-left">
        <Panel title={tx('ui.mobile.latest_activity')} detail={tx('ui.mobile.latest_activity_detail')}>
          <Feed items={feed} now={now} onOpen={onOpen} />
        </Panel>
        <div className="xo-mob-stack">
          <Panel title={tx('ui.mobile.their_places')} detail={tx('ui.mobile.their_places_detail')}>
            {data.activity.places.length === 0 ? <Empty title={tx('ui.mobile.no_location_data')} /> : (
              <>
                <PulseMap countries={[...new Set(data.activity.places.map((place) => place.country))].map((code) => ({ code, accounts: 1 }))}
                  pings={pings} height={230} fit />
                <ol className="xo-mob-places is-compact">
                  {data.activity.places.slice(0, 4).map((place) => (
                    <li key={`${place.city}-${place.country}-${place.zone}`}>
                      <Flag code={place.country} />
                      <span><strong>{place.city || place.country}</strong><small>{localClock(place.zone, now) || place.zone}</small></span>
                      <em>{ago(place.lastSeenAt, now)}</em>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </Panel>
          <Panel title={tx('ui.mobile.their_hours')} detail={tx('ui.mobile.their_hours_detail')}>
            <HourHeatmap grid={data.activity.heatmap} compact />
          </Panel>
        </div>
      </div>
      <Tiles items={tiles} onOpen={onOpen} />
    </>
  );
}

// ---------------------------------------------------------------- sessions

function SessionsSection({ data, now }) {
  return (
    <Panel title={tx('ui.mobile.their_sessions')} detail={tx('ui.mobile.their_sessions_detail')} className="is-player">
      <SessionPlayer sessions={data.activity.sessions} showPerson={false} now={now} />
    </Panel>
  );
}

// ---------------------------------------------------------------- devices and health

function DeviceCard({ device, diagnostics, selected, onSelect, now, as }) {
  const total = diagnostics ? device.syncs30.success + device.syncs30.timeout + device.syncs30.error + device.syncs30.aborted : 0;
  const finish = finishLabel(deviceLook(device.origin, device.name), as);
  const Tag = onSelect ? 'button' : 'div';
  return (
    <Tag type={onSelect ? 'button' : undefined} className={`xo-mob-device${selected ? ' is-selected' : ''} is-${device.status}`}
      onClick={onSelect ? () => onSelect(device.id) : undefined}>
      <DeviceArt origin={device.origin} name={device.name} size={64} className="is-tile" />
      <span className="xo-mob-device-body">
        <span className="xo-mob-device-title"><strong>{originLabel(device.origin)}</strong>
          <Pill tone={device.status === 'paired' ? 'ok' : 'flat'}>{tx(`ui.mobile.device_${device.status}`)}</Pill></span>
        <small>{[finish, device.macTail ? `…${device.macTail}` : null, tx('ui.mobile.paired_on', { date: dateOf(device.pairedAt) })].filter(Boolean).join(' · ')}</small>
        <span className="xo-mob-device-stats">
          <span><RefreshCw aria-hidden="true" />{device.lastSuccessAt ? ago(device.lastSuccessAt, now) : tx('ui.mobile.not_synced_yet')}</span>
          {device.battery !== null && <BatteryMark value={device.battery} charging={device.charging} />}
        </span>
        {diagnostics && device.lastSync && (
          <span className="xo-mob-device-diag">
            <span className="xo-mob-outcome-bar" title={tx('ui.mobile.sync_split_30d')}>
              {['success', 'timeout', 'error', 'aborted'].map((status) => device.syncs30[status] > 0 && (
                <i key={status} style={{ width: `${(device.syncs30[status] / Math.max(1, total)) * 100}%`, background: OUTCOMES[status] }} />
              ))}
            </span>
            <small>{total === 0 ? tx('ui.mobile.no_syncs_30d') : counted('ui.mobile.sync_summary', total, { rate: percent(device.syncs30.success, total) })}
              {' · '}{tx('ui.mobile.last_attempt', { status: tx(`ui.mobile.sync_${device.lastSync.status}`), when: ago(device.lastSync.at, now), took: span(device.lastSync.durationMs) })}</small>
          </span>
        )}
      </span>
    </Tag>
  );
}

function DevicesSection({ data, now, as }) {
  const wearables = data.devices.filter((device) => device.origin === 'r01' || device.origin === 'b01');
  const [selected, setSelected] = useState(() => {
    const first = wearables.find((device) => device.status === 'paired') || wearables[0];
    return first ? first.id : null;
  });
  const ingestionSeries = useMemo(() => {
    const byDate = new Map();
    for (const entry of data.ingestions) {
      if (!byDate.has(entry.at)) byDate.set(entry.at, { at: entry.at });
      byDate.get(entry.at)[entry.origin] = entry.submissions;
    }
    return [...byDate.values()].sort((a, b) => a.at - b.at);
  }, [data.ingestions]);
  const origins = [...new Set(data.ingestions.map((entry) => entry.origin))];
  const byMetric = useMemo(() => {
    const grouped = new Map();
    for (const entry of data.health) {
      if (!grouped.has(entry.metric)) grouped.set(entry.metric, []);
      grouped.get(entry.metric).push(entry);
    }
    return [...grouped.entries()];
  }, [data.health]);

  return (
    <>
      <div className="xo-mob-devices">
        {data.devices.length === 0 && <Panel><Empty title={tx('ui.mobile.no_devices')} /></Panel>}
        {data.devices.map((device) => (
          <DeviceCard key={device.id} device={device} diagnostics={data.diagnostics} now={now} as={as}
            selected={data.mayReadHealth && selected === device.id}
            onSelect={data.mayReadHealth && (device.origin === 'r01' || device.origin === 'b01') ? setSelected : null} />
        ))}
      </div>

      {data.mayReadHealth && selected !== null && <DeviceHealth key={selected} deviceId={selected} />}

      <div className="xo-mob-row is-halves">
        <Panel title={tx('ui.mobile.data_arriving')} detail={tx('ui.mobile.data_arriving_detail')}>
          {ingestionSeries.length === 0 ? <Empty title={tx('ui.mobile.no_uploads_30d')} /> : (
            <div className="xo-mob-chart is-short">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ingestionSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={CHART.grid} />
                  <XAxis dataKey="at" tickFormatter={day} tickLine={false} axisLine={false} minTickGap={20} tick={CHART.tick} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={36} />
                  <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
                    <ChartTip title={(at) => tipDay(at)}
                      rows={(point) => stackRows(point, origins.map((origin) => ({ key: origin, name: originLabel(origin), color: ORIGIN_COLORS[origin] || CHART.soft })), { sorted: true })}
                      facts={(point) => {
                        const all = origins.reduce((sum, origin) => sum + (point[origin] || 0), 0);
                        return [
                          { label: tx('ui.mobile.tip_uploads_total'), value: NUMBER.format(all) },
                          versusAverage(all, averageOf(ingestionSeries.map((entry) => origins.reduce((sum, origin) => sum + (entry[origin] || 0), 0)))),
                        ];
                      }} />
                  )} />
                  {origins.map((origin) => <Bar key={origin} dataKey={origin} stackId="o" fill={ORIGIN_COLORS[origin] || CHART.soft} name={originLabel(origin)} maxBarSize={20} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
        <Panel title={tx('ui.mobile.health_coverage')} detail={tx('ui.mobile.health_coverage_detail')}>
          {byMetric.length === 0 ? <Empty title={tx('ui.mobile.no_health_data')} /> : (
            <ol className="xo-mob-coverage">
              {byMetric.map(([metric, entries]) => (
                <li key={metric}>
                  <strong>{metricLabel(metric)}</strong>
                  <span>{entries.map((entry) => (
                    <em key={entry.origin}><i style={{ background: ORIGIN_COLORS[entry.origin] || CHART.soft }} />{originLabel(entry.origin)} · {COMPACT.format(entry.rows)} · {entry.lastAt ? ago(entry.lastAt, now) : '—'}</em>
                  ))}</span>
                </li>
              ))}
            </ol>
          )}
        </Panel>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- diet

function DietSection({ diet, now, as }) {
  const logged = diet.days.filter((entry) => entry.meals > 0);
  const average = logged.length === 0 ? null : logged.reduce((sum, entry) => sum + entry.kcal, 0) / logged.length;
  const watered = diet.water.filter((entry) => entry.liters > 0);
  const water = watered.length === 0 ? null : watered.reduce((sum, entry) => sum + entry.liters, 0) / watered.length;
  const macros = diet.meals.reduce((sum, meal) => ({
    protein: sum.protein + (meal.protein === null ? 0 : meal.protein),
    carbs: sum.carbs + (meal.carbs === null ? 0 : meal.carbs),
    fat: sum.fat + (meal.fat === null ? 0 : meal.fat),
  }), { protein: 0, carbs: 0, fat: 0 });
  const grams = macros.protein + macros.carbs + macros.fat;
  const liters = new Intl.NumberFormat(undefined, { style: 'unit', unit: 'liter', maximumFractionDigits: 1 });
  const gramsFormat = new Intl.NumberFormat(undefined, { style: 'unit', unit: 'gram', maximumFractionDigits: 0 });
  const meals = diet.meals;
  const hasWater = watered.length > 0;
  if (diet.meals.length === 0 && !hasWater) {
    return <Panel><Empty title={tx('ui.mobile.no_meals')} detail={tx('ui.mobile.no_meals_detail')} /></Panel>;
  }
  return (
    <>
      <Tiles items={[
        { label: tx('ui.mobile.meals_30d'), value: NUMBER.format(diet.meals.length), detail: counted('ui.mobile.days_logged', logged.length) },
        { label: tx('ui.mobile.per_logged_day'), value: average === null ? '—' : tx('ui.mobile.value_kcal', { value: NUMBER.format(Math.round(average)) }), detail: tx('ui.mobile.per_logged_day_detail') },
        { label: tx('ui.mobile.calorie_goal'), value: diet.calorieGoal === null ? '—' : tx('ui.mobile.value_kcal', { value: NUMBER.format(diet.calorieGoal) }), detail: diet.calorieGoal === null ? tx('ui.mobile.no_goal_set') : null },
        { label: tx('ui.mobile.water_per_day'), value: water === null ? '—' : liters.format(water), detail: counted('ui.mobile.days_logged', watered.length) },
      ]} />
      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={tx('ui.mobile.calories_daily')} detail={diet.calorieGoal === null ? tx('ui.mobile.calories_daily_detail') : tx('ui.mobile.calories_daily_goal_detail')}>
          <div className="xo-mob-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={diet.days} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={CHART.grid} />
                <XAxis dataKey="at" tickFormatter={day} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
                <YAxis tickLine={false} axisLine={false} tick={CHART.tick} width={44} tickFormatter={(value) => COMPACT.format(value)} />
                <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
                  <ChartTip title={(at) => tipDay(at)}
                    rows={(point) => [
                      { name: tx('ui.mobile.calories'), color: CHART.ink, text: tx('ui.mobile.value_kcal', { value: NUMBER.format(Math.round(point.kcal)) }) },
                    ]}
                    facts={(point) => {
                      const goal = diet.calorieGoal;
                      const gap = goal !== null && point.kcal > 0 ? Math.round(point.kcal - goal) : null;
                      return [
                        { label: tx('ui.mobile.tip_meals_logged'), value: NUMBER.format(point.meals) },
                        gap === null ? null : {
                          label: tx('ui.mobile.tip_vs_goal'),
                          value: `${gap > 0 ? '+' : gap < 0 ? '−' : ''}${tx('ui.mobile.value_kcal', { value: NUMBER.format(Math.abs(gap)) })} · ${percent(point.kcal, goal)}`,
                          tone: Math.abs(gap) / goal > 0.15 ? (gap > 0 ? 'down' : 'up') : null,
                        },
                        point.kcal > 0 ? versusAverage(point.kcal, average) : null,
                      ];
                    }} />
                )} />
                <Bar dataKey="kcal" fill={CHART.ink} radius={[3, 3, 0, 0]} maxBarSize={18} />
                {diet.calorieGoal !== null && <ReferenceLine y={diet.calorieGoal} stroke={CHART.mid} strokeDasharray="4 4" />}
              </BarChart>
            </ResponsiveContainer>
            <div className="xo-mob-key">
              <span><i style={{ background: CHART.ink }} />{tx('ui.mobile.calories')}</span>
              {diet.calorieGoal !== null && <span><i className="is-dash" />{tx('ui.mobile.calorie_goal')}</span>}
            </div>
          </div>
        </Panel>
        <Panel title={tx('ui.mobile.macros_title')} detail={tx('ui.mobile.macros_detail')}>
          {grams === 0 ? <Empty title={tx('ui.mobile.no_macros')} /> : (
            <div className="xo-mob-macros">
              <span className="xo-mob-macro-bar" aria-hidden="true">
                {['protein', 'carbs', 'fat'].map((macro) => <i key={macro} className={`is-${macro}`} style={{ flexGrow: macros[macro] }} />)}
              </span>
              <ol>
                {['protein', 'carbs', 'fat'].map((macro) => (
                  <li key={macro}>
                    <i className={`is-${macro}`} />
                    <span>{tx(`ui.mobile.macro_${macro}`)}</span>
                    <strong>{percent(macros[macro], grams)}</strong>
                    <small>{tx('ui.mobile.per_day_value', { value: gramsFormat.format(macros[macro] / Math.max(1, logged.length)) })}</small>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {hasWater && (
            <div className="xo-mob-water">
              <h3 className="xo-mob-subhead"><Droplets aria-hidden="true" />{tx('ui.mobile.water_title')}</h3>
              <span className="xo-mob-water-days" aria-hidden="true">
                {diet.water.map((entry) => <i key={entry.at} style={{ height: `${Math.min(100, (entry.liters / Math.max(0.5, ...diet.water.map((point) => point.liters))) * 100)}%` }} />)}
              </span>
            </div>
          )}
        </Panel>
      </div>
      {meals.length > 0 && (
        <Panel title={tx('ui.mobile.meals')} detail={tx('ui.mobile.meals_detail')}>
          <div className="xo-mob-meals">
            {byDay(meals, now).map((group) => (
              <div key={group.heading} className="xo-mob-feed-day">
                <h4>{group.heading}</h4>
                <ol>
                  {group.items.map((meal, index) => (
                    <li key={`${meal.at}-${index}`} className="xo-mob-meal">
                      <time>{clock(meal.at)}</time>
                      <span className="xo-mob-meal-main">
                        <strong>{mealName(meal.type, as)}</strong>
                        <small>{meal.items.slice(0, 5).map((item) => [item.food, item.grams === null ? null : gramsFormat.format(item.grams)].filter(Boolean).join(' ')).join(' · ')}
                          {meal.items.length > 5 ? ` · ${tx('ui.mobile.and_more', { count: NUMBER.format(meal.items.length - 5) })}` : ''}</small>
                      </span>
                      <span className="xo-mob-meal-macros">
                        {['protein', 'carbs', 'fat'].map((macro) => meal[macro] !== null && (
                          <em key={macro}><i className={`is-${macro}`} />{gramsFormat.format(meal[macro])}</em>
                        ))}
                      </span>
                      <b>{meal.kcal === null ? '—' : tx('ui.mobile.value_kcal', { value: NUMBER.format(Math.round(meal.kcal)) })}</b>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </>
  );
}

// ---------------------------------------------------------------- habits

// A habit's time is minutes after midnight, the time of day the person set for it.
function timeOfDay(minute) {
  return new Date(Date.UTC(2024, 0, 1, Math.floor(minute / 60), minute % 60))
    .toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
}

function runs(days) {
  let best = 0;
  let current = 0;
  for (const entry of days) {
    current = entry.done > 0 ? current + 1 : 0;
    best = Math.max(best, current);
  }
  return { best, current };
}

function HabitsSection({ habits, life, now }) {
  const active = habits.list.filter((habit) => habit.removedAt === null);
  const days = habits.days.filter((entry) => entry.done > 0).length;
  const streak = runs(habits.days);
  const most = Math.max(1, ...habits.days.map((entry) => entry.done));
  if (habits.list.length === 0) return <Panel><Empty title={tx('ui.mobile.no_habits')} detail={tx('ui.mobile.no_habits_detail')} /></Panel>;
  return (
    <>
      <Tiles items={[
        { label: tx('ui.mobile.habits_active_label'), value: NUMBER.format(active.length), detail: counted('ui.mobile.habits_removed', habits.list.length - active.length) },
        { label: tx('ui.mobile.habits_done_30d'), value: NUMBER.format(life.completions30), detail: counted('ui.mobile.days_with_habit', days) },
        { label: tx('ui.mobile.current_streak'), value: counted('ui.mobile.days_count', streak.current), detail: tx('ui.mobile.best_streak', { days: counted('ui.mobile.days_count', streak.best) }) },
        { label: tx('ui.mobile.last_ticked'), value: habits.recent[0] ? ago(habits.recent[0].at, now) : '—', detail: habits.recent[0] ? habits.recent[0].name : null },
      ]} />
      <Panel title={tx('ui.mobile.habit_days_title')} detail={tx('ui.mobile.habit_days_detail')}>
        <div className="xo-mob-day-strip">
          {habits.days.map((entry) => (
            <span key={entry.at} title={`${day(entry.at)} · ${counted('ui.mobile.done_count', entry.done)}`}
              style={{ '--level': entry.done === 0 ? 0 : (0.25 + (entry.done / most) * 0.75).toFixed(3) }} className={entry.done === 0 ? 'is-empty' : ''} />
          ))}
        </div>
        <div className="xo-mob-day-strip-axis"><span>{habits.days[0] ? day(habits.days[0].at) : ''}</span><span>{tx('ui.mobile.today')}</span></div>
      </Panel>
      <div className="xo-mob-row is-wide-left">
        <Panel title={tx('ui.mobile.their_habits')} detail={tx('ui.mobile.their_habits_detail')}>
          <ol className="xo-mob-habits">
            {habits.list.map((habit, index) => (
              <li key={`${habit.name}-${habit.createdAt}-${index}`} className={habit.removedAt === null ? '' : 'is-faded'}>
                <span className="xo-mob-rank-icon"><ListChecks aria-hidden="true" /></span>
                <span className="xo-mob-rank-main">
                  <strong>{habit.name}</strong>
                  <small>{[habit.goalMinutes === null ? null : tx('ui.mobile.habit_time', { time: timeOfDay(habit.goalMinutes) }),
                    habit.removedAt === null ? tx('ui.mobile.since_date', { date: dateOf(habit.createdAt) }) : tx('ui.mobile.removed_on', { date: dateOf(habit.removedAt) })].filter(Boolean).join(' · ')}</small>
                </span>
                <span className="xo-mob-habit-done">
                  <span className="xo-mob-share"><i style={{ width: `${Math.min(100, (habit.done30 / 30) * 100)}%` }} /></span>
                  <small>{counted('ui.mobile.done_count', habit.done30)}</small>
                </span>
                <em>{habit.lastDoneAt ? ago(habit.lastDoneAt, now) : tx('ui.mobile.never')}</em>
              </li>
            ))}
          </ol>
        </Panel>
        <Panel title={tx('ui.mobile.recent_ticks')} detail={tx('ui.mobile.recent_ticks_detail')}>
          {habits.recent.length === 0 ? <Empty title={tx('ui.mobile.no_ticks')} /> : (
            <ol className="xo-mob-mails">
              {habits.recent.map((tick, index) => (
                <li key={`${tick.at}-${index}`}><span><strong>{tick.name}</strong><small>{when(tick.at)}</small></span><em>{ago(tick.at, now)}</em></li>
              ))}
            </ol>
          )}
        </Panel>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- workouts and games

function WorkoutsSection({ workouts, now, as }) {
  if (workouts.length === 0) return <Panel><Empty title={tx('ui.mobile.no_workouts')} detail={tx('ui.mobile.no_workouts_detail')} /></Panel>;
  const finished = workouts.filter((workout) => workout.minutes !== null);
  const minutes = finished.reduce((sum, workout) => sum + workout.minutes, 0);
  const kcal = finished.reduce((sum, workout) => sum + (workout.kcal === null ? 0 : workout.kcal), 0);
  const kinds = new Map();
  for (const workout of workouts) kinds.set(workout.type, (kinds.has(workout.type) ? kinds.get(workout.type) : 0) + 1);
  const favourite = [...kinds.entries()].sort((a, b) => b[1] - a[1])[0];
  return (
    <>
      <Tiles items={[
        { label: tx('ui.mobile.workouts'), value: NUMBER.format(workouts.length), detail: tx('ui.mobile.latest_value', { when: ago(workouts[0].startedAt, now) }) },
        { label: tx('ui.mobile.time_moving'), value: minutes === 0 ? '—' : lengthOf(minutes * MINUTE),
          detail: minutes === 0 ? null : tx('ui.mobile.per_workout', { length: lengthOf((minutes / finished.length) * MINUTE) }) },
        { label: tx('ui.mobile.calories_burned'), value: kcal === 0 ? '—' : tx('ui.mobile.value_kcal', { value: NUMBER.format(kcal) }), detail: tx('ui.mobile.estimated_by_app') },
        { label: tx('ui.mobile.most_done'), value: workoutName(favourite[0], as), detail: counted('ui.mobile.times_count', favourite[1]) },
      ]} />
      <Panel title={tx('ui.mobile.their_workouts')} detail={tx('ui.mobile.their_workouts_detail')}>
        <ol className="xo-mob-rank">
          {workouts.map((workout, index) => (
            <li key={`${workout.startedAt}-${index}`}>
              <span className="xo-mob-rank-icon"><Activity aria-hidden="true" /></span>
              <span className="xo-mob-rank-main"><strong>{workoutName(workout.type, as)}</strong><small>{when(workout.startedAt)}</small></span>
              <span className="xo-mob-cell-muted">{workout.kcal === null || workout.kcal === 0 ? '' : tx('ui.mobile.value_kcal', { value: NUMBER.format(workout.kcal) })}</span>
              <em>{workout.minutes === null ? <Pill tone="info">{tx('ui.mobile.still_going')}</Pill> : workoutLength(workout.minutes)}</em>
            </li>
          ))}
        </ol>
      </Panel>
    </>
  );
}

function GamesSection({ games, now, as }) {
  if (games.totals.length === 0) return <Panel><Empty title={tx('ui.mobile.no_games')} detail={tx('ui.mobile.no_games_detail')} /></Panel>;
  return (
    <>
      <div className="xo-mob-game-cards">
        {games.totals.map((game) => (
          <article key={game.game} className="xo-mob-game-card">
            <span className="xo-mob-rank-icon"><Gamepad2 aria-hidden="true" /></span>
            <strong>{gameName(game.game, as)}</strong>
            <dl>
              <div><dt>{tx('ui.mobile.plays')}</dt><dd>{NUMBER.format(game.plays)}</dd></div>
              <div><dt><Trophy aria-hidden="true" />{tx('ui.mobile.best_score')}</dt><dd>{NUMBER.format(game.best)}</dd></div>
            </dl>
            <small>{tx('ui.mobile.last_played', { when: ago(game.lastPlayedAt, now) })}</small>
          </article>
        ))}
      </div>
      <Panel title={tx('ui.mobile.recent_scores')} detail={tx('ui.mobile.recent_scores_detail')}>
        <ol className="xo-mob-mails">
          {games.recent.map((play, index) => (
            <li key={`${play.at}-${index}`}>
              <span><strong>{gameName(play.game, as)}</strong><small>{when(play.at)}</small></span>
              <em>{tx('ui.mobile.score_value', { score: NUMBER.format(play.score) })}</em>
            </li>
          ))}
        </ol>
      </Panel>
    </>
  );
}

// ---------------------------------------------------------------- orders, kits and payments

function paymentPill(payment) {
  if (payment === null) return <Pill tone="flat">{tx('ui.mobile.no_payment')}</Pill>;
  if (payment.refundedMinor > 0) {
    return <Pill tone="warn">{payment.refundedMinor >= payment.amountMinor ? tx('ui.economics.refunded') : tx('ui.mobile.partly_refunded')}</Pill>;
  }
  const tone = payment.status === 'succeeded' ? 'ok' : payment.status === 'canceled' ? 'bad' : 'info';
  return <Pill tone={tone}>{codeLabel('paymentStatus', payment.status)}</Pill>;
}

function OrdersSection({ commerce, now }) {
  if (commerce.orders.length === 0) return <Panel><Empty title={tx('ui.mobile.no_orders')} detail={tx('ui.mobile.no_orders_detail')} /></Panel>;
  return (
    <div className="xo-mob-orders">
      {commerce.orders.map((order) => (
        <article key={order.trackingCode} className="xo-mob-order">
          <header>
            <span className="xo-mob-rank-icon"><ShoppingBag aria-hidden="true" /></span>
            <span className="xo-mob-order-id">
              <strong className="xo-mono">{reference(order.trackingCode)}</strong>
              <small>{[tx('ui.mobile.placed_on', { date: when(order.placedAt || order.createdAt) }), order.source ? tx(`ui.mobile.order_source_${order.source}`) : null].filter(Boolean).join(' · ')}</small>
            </span>
            <span className="xo-mob-order-state">
              {commerce.mayReadPayments && paymentPill(order.payment)}
              {order.shipment && <Pill tone={order.shipment.status === 'delivered' ? 'ok' : 'info'}>{codeLabel('shipmentStatus', order.shipment.status)}</Pill>}
            </span>
            <strong className="xo-mob-order-total">{money(order.totalMinor, order.currency)}</strong>
          </header>
          <div className="xo-mob-order-body">
            <ol className="xo-mob-order-items">
              {order.items.map((item, index) => (
                <li key={`${item.name}-${index}`}>
                  <span>{item.name || tx('ui.mobile.product')}</span>
                  <small>× {NUMBER.format(item.quantity)}</small>
                  <b>{money(item.unitPriceMinor * item.quantity - item.discountMinor, item.currency)}</b>
                </li>
              ))}
            </ol>
            <dl className="xo-mob-order-sums">
              <div><dt>{tx('ui.economics.subtotal')}</dt><dd>{money(order.subtotalMinor, order.currency)}</dd></div>
              {order.discountMinor > 0 && <div><dt>{order.coupon ? tx('ui.mobile.discount_with', { coupon: order.coupon }) : tx('ui.economics.discounts')}</dt><dd>−{money(order.discountMinor, order.currency)}</dd></div>}
              <div><dt>{tx('ui.economics.shipping')}</dt><dd>{money(order.shippingMinor, order.currency)}</dd></div>
              {order.taxMinor > 0 && <div><dt>{tx('ui.economics.tax')}</dt><dd>{money(order.taxMinor, order.currency)}</dd></div>}
              <div className="is-total"><dt>{tx('ui.orders.order_total_2f32f200')}</dt><dd>{money(order.totalMinor, order.currency)}</dd></div>
            </dl>
          </div>
          <footer>
            {order.country && <span><MapPin aria-hidden="true" /><Flag code={order.country} />{order.city || ''}</span>}
            {order.shipment && order.shipment.trackingNumber && <span>{tx('ui.orders.tracking_e81a151b')} <b className="xo-mono">{order.shipment.trackingNumber}</b></span>}
            {order.shipment && order.shipment.dispatchedAt && <span>{tx('ui.mobile.dispatched_ago', { when: ago(order.shipment.dispatchedAt, now) })}</span>}
            {order.shipment && order.shipment.deliveredAt && <span>{tx('ui.mobile.delivered_ago', { when: ago(order.shipment.deliveredAt, now) })}</span>}
            {commerce.mayReadPayments && order.payment && order.payment.failureMessage && <span className="is-bad">{order.payment.failureMessage}</span>}
          </footer>
        </article>
      ))}
    </div>
  );
}

const KIT_STEPS = [['createdAt', 'ui.mobile.kit_created'], ['activatedAt', 'ui.mobile.kit_activated'], ['collectedAt', 'ui.mobile.kit_collected']];

function KitsSection({ commerce, now }) {
  if (commerce.kits.length === 0) return <Panel><Empty title={tx('ui.mobile.no_kits')} detail={tx('ui.mobile.no_kits_detail')} /></Panel>;
  return (
    <Panel title={tx('ui.mobile.their_kits')} detail={tx('ui.mobile.their_kits_detail')}>
      <ol className="xo-mob-kits">
        {commerce.kits.map((kit) => (
          <li key={kit.code}>
            <span className="xo-mob-rank-icon"><Package aria-hidden="true" /></span>
            <span className="xo-mob-kit-main">
              <strong>{kit.product || tx('ui.mobile.kit')}</strong>
              <small><span className="xo-mono">{kit.code}</span>
                {kit.inApp && <em className="xo-mob-tag">{tx('ui.mobile.kit_in_app')}</em>}
                {kit.forPatient && <em className="xo-mob-tag">{tx('ui.mobile.kit_for_patient')}</em>}</small>
            </span>
            <ol className="xo-mob-kit-steps">
              {KIT_STEPS.map(([field, label]) => (
                <li key={field} className={kit[field] ? 'is-done' : ''}>
                  <i aria-hidden="true" />
                  <span>{tx(label)}</span>
                  <small>{kit[field] ? dateOf(kit[field]) : '—'}</small>
                </li>
              ))}
            </ol>
            <span className="xo-mob-kit-where">
              {kit.country && <span><Flag code={kit.country} />{kit.city || ''}</span>}
              <small>{ago(kit.collectedAt || kit.activatedAt || kit.createdAt, now)}</small>
            </span>
          </li>
        ))}
      </ol>
    </Panel>
  );
}

function PaymentsSection({ commerce }) {
  if (commerce.payments.length === 0) return <Panel><Empty title={tx('ui.mobile.no_payments')} detail={tx('ui.mobile.no_payments_detail')} /></Panel>;
  const collected = commerce.payments.filter((payment) => payment.status === 'succeeded');
  const currencies = [...new Set(collected.map((payment) => payment.currency))];
  return (
    <>
      <Tiles items={[
        { label: tx('ui.mobile.payments_label'), value: NUMBER.format(commerce.payments.length), detail: counted('ui.mobile.succeeded_count', collected.length) },
        ...currencies.slice(0, 3).map((currency) => ({
          label: tx('ui.economics.net_collected'),
          value: money(collected.filter((payment) => payment.currency === currency).reduce((sum, payment) => sum + payment.amountMinor - payment.refundedMinor, 0), currency),
          detail: currency,
        })),
      ]} />
      <Panel title={tx('ui.mobile.their_payments')} detail={tx('ui.mobile.their_payments_detail')}>
        <div className="xo-mob-payments" role="table">
          <div className="xo-mob-payments-head" role="row">
            <span>{tx('ui.mobile.date')}</span><span>{tx('ui.mobile.order')}</span><span>{tx('ui.mobile.provider')}</span>
            <span>{tx('ui.mobile.status')}</span><span>{tx('ui.mobile.amount')}</span>
          </div>
          {commerce.payments.map((payment, index) => (
            <div key={`${payment.createdAt}-${index}`} className="xo-mob-payments-row" role="row">
              <span>{when(payment.createdAt)}</span>
              <span className="xo-mono">{reference(payment.order)}</span>
              <span>{payment.provider ? payment.provider.charAt(0).toUpperCase() + payment.provider.slice(1) : '—'}</span>
              <span>{paymentPill(payment)}{payment.failureMessage && <small className="is-bad">{payment.failureMessage}</small>}</span>
              <span className="xo-mob-payments-amount">
                <strong>{money(payment.amountMinor, payment.currency)}</strong>
                {payment.refundedMinor > 0 && <small>{tx('ui.mobile.refunded_value', { amount: money(payment.refundedMinor, payment.currency) })}</small>}
              </span>
            </div>
          ))}
        </div>
      </Panel>
    </>
  );
}

// ---------------------------------------------------------------- notifications, security

// Silent sync nudges are the app talking to itself; they are counted in one line
// rather than listed among what the person actually saw.
function MessagesSection({ data, now }) {
  const visible = data.notifications.filter((notification) => !notification.silent);
  const silent = data.notifications.filter((notification) => notification.silent);
  return (
    <div className="xo-mob-row is-halves">
      <Panel title={tx('ui.mobile.push_received')} detail={tx('ui.mobile.push_received_detail')}>
        {silent.length > 0 && <p className="xo-mob-quiet-line"><RefreshCw aria-hidden="true" />{counted('ui.mobile.silent_count', silent.length, { when: ago(silent[0].at, now) })}</p>}
        {visible.length === 0 ? <Empty title={tx('ui.mobile.no_visible_pushes')} /> : (
          <div className="xo-mob-push-list">{visible.map((notification, index) => <PushCard key={`${notification.at}-${index}`} push={notification} now={now} />)}</div>
        )}
      </Panel>
      <Panel title={tx('ui.mobile.emails_sent')} detail={tx('ui.mobile.emails_sent_detail')}>
        {data.messages.length === 0 ? <Empty title={tx('ui.mobile.no_emails')} /> : (
          <ol className="xo-mob-mails">
            {data.messages.map((message, index) => (
              <li key={`${message.at}-${index}`}>
                <span><strong>{message.subject || emailReasonLabel(message.reason)}</strong><small>{emailReasonLabel(message.reason)} · {message.destination}</small></span>
                {message.status !== 'sent' && <Pill tone="flat">{tx(`ui.mobile.email_${message.status}`)}</Pill>}
                <em>{ago(message.at, now)}</em>
              </li>
            ))}
          </ol>
        )}
      </Panel>
    </div>
  );
}

function ProviderMark({ provider }) {
  if (provider === 'google') return <FaGoogle aria-hidden="true" />;
  if (provider === 'apple') return <FaApple aria-hidden="true" />;
  return <KeyRound aria-hidden="true" />;
}

function SecuritySection({ data, now }) {
  return (
    <div className="xo-mob-row is-halves">
      <div className="xo-mob-stack">
        <Panel title={tx('ui.mobile.sign_in')} detail={tx('ui.mobile.sign_in_detail')}>
          <Facts rows={[
            [tx('ui.mobile.password'), data.account.hasPassword ? tx('ui.mobile.password_set') : tx('ui.mobile.no_password')],
            [tx('ui.mobile.email_verified'), data.account.emailVerifiedAt ? when(data.account.emailVerifiedAt) : tx('ui.mobile.not_verified')],
            [tx('ui.mobile.last_login'), when(data.account.lastLoginAt)],
          ]} />
          {data.identities.length > 0 && (
            <ol className="xo-mob-list">
              {data.identities.map((identity) => (
                <li key={`${identity.provider}-${identity.createdAt}`}><ProviderMark provider={identity.provider} />
                  <span><strong>{tx(`ui.mobile.method_${identity.provider}`)}</strong><small>{identity.email}</small></span>
                  <em>{identity.lastLoginAt ? ago(identity.lastLoginAt, now) : '—'}</em></li>
              ))}
            </ol>
          )}
        </Panel>
        <Panel title={tx('ui.mobile.invitations')} detail={tx('ui.mobile.invitations_detail')}>
          {data.invitations.length === 0 ? <Empty title={tx('ui.mobile.no_invitations')} /> : (
            <ol className="xo-mob-list">
              {data.invitations.map((invitation) => (
                <li key={invitation.createdAt}><Send aria-hidden="true" />
                  <span><strong>{tx(`ui.mobile.invitation_${invitation.status}`)}</strong>
                    <small>{tx('ui.mobile.invitation_meta', { attempts: invitation.attempts, max: invitation.maxAttempts, expires: when(invitation.expiresAt) })}</small></span>
                  <em>{ago(invitation.createdAt, now)}</em></li>
              ))}
            </ol>
          )}
        </Panel>
      </div>
      <div className="xo-mob-stack">
        <Panel title={tx('ui.mobile.signed_in_devices')} detail={tx('ui.mobile.signed_in_devices_detail')}>
          {data.sessions.length === 0 ? <Empty title={tx('ui.mobile.no_signed_in_devices')} /> : (
            <ol className="xo-mob-list">
              {data.sessions.map((session) => (
                <li key={session.id} className={session.revokedAt ? 'is-faded' : ''}>
                  <PlatformMark platform={session.platform} />
                  <span><strong>{tx(`ui.mobile.method_${session.method}`)}{session.label ? ` · ${session.label}` : ''}</strong>
                    <small>{tx('ui.mobile.session_meta', { started: when(session.createdAt), used: session.lastUsedAt ? ago(session.lastUsedAt, now) : '—' })}</small></span>
                  {session.revokedAt ? <code>{session.revocation || 'REVOKED'}</code> : <Pill tone={session.expiresAt > now ? 'ok' : 'flat'}>{session.expiresAt > now ? tx('ui.mobile.session_live') : tx('ui.mobile.session_expired')}</Pill>}
                </li>
              ))}
            </ol>
          )}
          {data.pushTokens.length > 0 && (
            <p className="xo-mob-footnote">{tx('ui.mobile.push_tokens_line', { list: data.pushTokens.map((token) => `${token.platform} · ${ago(token.updatedAt, now)}`).join(', ') })}</p>
          )}
        </Panel>
        <Panel title={tx('ui.mobile.auth_events')} detail={tx('ui.mobile.auth_events_detail')}>
          {data.authEvents.length === 0 ? <Empty title={tx('ui.mobile.no_auth_events')} /> : (
            <ol className="xo-mob-auth-list">
              {data.authEvents.map((event, index) => (
                <li key={`${event.at}-${index}`} className={/FAILED|LOCKED|REUSE/.test(event.event) ? 'is-bad' : ''}>
                  <code>{event.event}</code><small>{[event.provider, event.reason, event.purpose].filter(Boolean).join(' · ')}</small><em>{ago(event.at, now)}</em>
                </li>
              ))}
            </ol>
          )}
        </Panel>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- the page

const NO_COMMERCE = { mayReadOrders: false, mayReadPayments: false, mayReadKits: false, orders: [], payments: [], kits: [] };

export default function MobileAccountProfile({ uuid, onBack }) {
  const { permissions } = useXomicsAuth();
  const mayManage = permissions.includes('PATIENT_IDENTITY_MANAGE');
  const mayManageExamples = permissions.includes('PATIENT_EXAMPLE_DATA_MANAGE');
  const load = useCallback(() => loadMobileAccount(uuid), [uuid]);
  const profile = useLiveResource(['mobile'], load, { reconcileMs: 60000 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [linking, setLinking] = useState(false);
  const [record, setRecord] = useState(false);
  const [resend, setResend] = useState({ status: 'idle', message: null });
  const now = useNow(30000);
  const as = useAppStrings();

  const open = useCallback((section) => {
    const next = new URLSearchParams(searchParams);
    if (section === 'overview') next.delete('section'); else next.set('section', section);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  if (profile.loading && profile.data === null) return <Loading label={tx('ui.mobile.reading_account')} />;
  if (profile.error && profile.data === null) return <Failed message={profile.error} onRetry={profile.refresh} />;
  const data = profile.data;
  const commerce = data.commerce ? data.commerce : NO_COMMERCE;
  const name = data.profile.name || data.account.email || tx('ui.mobile.no_name_yet');
  const platform = data.sessions[0]?.platform || null;
  const place = data.activity.places[0] || null;
  const openInvitation = data.invitations.find((invitation) => invitation.status === 'active');
  const linkedPatient = data.patient && !data.patient.restricted && !data.patient.missing ? data.patient : null;
  const localTime = localClock(data.activity.timezone || (place ? place.zone : null), now);
  const latest = data.activity.sessions[0] || null;
  const lastStep = latest && latest.steps.length > 0 ? latest.steps[latest.steps.length - 1] : null;
  const inApp = latest !== null && now - latest.end <= LIVE_MS && !(lastStep && lastStep.name === 'App.Background');

  const hidden = new Set([
    ...(commerce.mayReadOrders ? [] : ['orders']),
    ...(commerce.mayReadKits ? [] : ['kits']),
    ...(commerce.mayReadPayments ? [] : ['payments']),
  ]);
  const asked = searchParams.get('section');
  const section = asked && SECTIONS.flat().some(([key]) => key === asked) && !hidden.has(asked) ? asked : 'overview';
  const counts = {
    sessions: data.activity.sessions.length,
    devices: data.devices.filter((device) => device.status === 'paired').length,
    diet: data.diet.meals.length,
    habits: data.habits.list.filter((habit) => habit.removedAt === null).length,
    workouts: data.workouts.length,
    games: data.games.totals.reduce((sum, game) => sum + game.plays, 0),
    orders: commerce.orders.length,
    kits: commerce.kits.length,
    payments: commerce.payments.length,
    messages: data.notifications.filter((notification) => !notification.silent).length + data.messages.length,
  };

  async function sendAgain() {
    setResend({ status: 'sending', message: null });
    try {
      const sent = await resendMobileInvitation(data.account.email);
      setResend({ status: 'sent', message: tx('ui.mobile.invite_sent_detail', { date: new Date(sent.expiresAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) }) });
      profile.refresh();
    } catch (caught) {
      setResend({ status: 'failed', message: errorMessage(caught) });
    }
  }

  return (
    <div className="xo-platform-page xo-mobile-page xo-mob-profile">
      <section className="xo-mob-profile-head">
      <header className="xo-mob-identity">
        <button type="button" className="xo-mob-back-chevron" onClick={onBack} aria-label={tx('ui.mobile.back_to_accounts')} title={tx('ui.mobile.back_to_accounts')}>
          <ChevronLeft aria-hidden="true" />
        </button>
        <span className="xo-mob-avatar is-large">{initials(data.profile.name, data.account.email)}</span>
        <div className="xo-mob-identity-text">
          <span className="xo-mob-identity-name"><h1>{name}</h1><Pill tone={statusTone(data.account.status)}>{tx(`ui.mobile.status_${data.account.status}`)}</Pill></span>
          <ul className="xo-mob-identity-chips">
            {data.account.email && (
              <li className="xo-mob-identity-email"><Mail aria-hidden="true" />{data.account.email}<Copy value={data.account.email} label={tx('ui.mobile.copy')} /></li>
            )}
            <li className={inApp ? 'is-live' : ''}>
              {inApp ? <><i className="xo-mob-live-dot" aria-hidden="true" />{tx('ui.mobile.in_app_title')}</>
                : tx('ui.mobile.last_seen_value', { when: ago(data.activity.lastSeenAt || data.account.lastLoginAt, now) })}
            </li>
            {platform && <li><PlatformMark platform={platform} withLabel />{data.activity.build ? <span>{versionLabel(data.activity.build)}</span> : null}</li>}
            {place && <li><Flag code={place.country} />{place.city}{localTime ? <span>{localTime}</span> : null}</li>}
            {data.account.language && <li>{String(data.account.language).toUpperCase()}</li>}
            <li>{tx('ui.mobile.joined_on', { date: dateOf(data.account.createdAt) })}</li>
          </ul>
          {mayManage && (data.account.status === 'invited' || openInvitation) && (
            <div className="xo-mob-actions">
              <button type="button" className="xo-btn" disabled={resend.status === 'sending'} onClick={sendAgain}><Send aria-hidden="true" />{tx('ui.mobile.resend_invitation')}</button>
              {resend.message && <small className={`xo-mob-resend t-${resend.status === 'sent' ? 'ok' : 'bad'}`}>{resend.message}</small>}
            </div>
          )}
        </div>
        <PatientBox data={data} mayManage={mayManage} onLink={() => setLinking(true)} onOpenRecord={() => setRecord(true)} />
      </header>
      <SectionNav value={section} onChange={open} counts={counts} hidden={hidden} />
      </section>

      <AnimatePresence mode="wait">
        <motion.div key={section} className="xo-mob-section" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.16 }}>
          {section === 'overview' && <OverviewSection data={data} commerce={commerce} now={now} as={as} onOpen={open} />}
          {section === 'sessions' && <SessionsSection data={data} now={now} />}
          {section === 'devices' && <DevicesSection data={data} now={now} as={as} />}
          {section === 'diet' && <DietSection diet={data.diet} now={now} as={as} />}
          {section === 'habits' && <HabitsSection habits={data.habits} life={data.life} now={now} />}
          {section === 'workouts' && <WorkoutsSection workouts={data.workouts} now={now} as={as} />}
          {section === 'games' && <GamesSection games={data.games} now={now} as={as} />}
          {section === 'orders' && <OrdersSection commerce={commerce} now={now} />}
          {section === 'kits' && <KitsSection commerce={commerce} now={now} />}
          {section === 'payments' && <PaymentsSection commerce={commerce} />}
          {section === 'messages' && <MessagesSection data={data} now={now} />}
          {section === 'security' && <SecuritySection data={data} now={now} />}
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {linking && <PatientLinkDialog account={data.account} current={linkedPatient || (data.patient ? { id: data.patient.id } : null)}
          onClose={() => setLinking(false)} onChanged={profile.refresh} />}
      </AnimatePresence>
      {record && linkedPatient && (
        <PatientDetailModal patient={{ id: linkedPatient.id, name: linkedPatient.name, subProject: linkedPatient.subproject.code }}
          mayManageExamples={mayManageExamples} onClose={() => setRecord(false)} onEdited={profile.refresh} />
      )}
    </div>
  );
}
